import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Users,
  UserPlus,
  UserCheck,
  TrendingUp,
  BarChart3,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  Upload,
  FileSpreadsheet,
  X,
  Database,
  MapPin,
  Building2,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Filter,
  Download,
  Eye,
  Trash2,
  ChevronRight,
  Globe,
  Target,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatNumber = (num) => {
  return new Intl.NumberFormat("pt-BR").format(num || 0);
};

const formatPercent = (num) => {
  return `${(num || 0).toFixed(1)}%`;
};

// Dados dos estados brasileiros
const ESTADOS_BRASIL = {
  AC: { nome: "Acre", regiao: "Norte" },
  AL: { nome: "Alagoas", regiao: "Nordeste" },
  AP: { nome: "Amapá", regiao: "Norte" },
  AM: { nome: "Amazonas", regiao: "Norte" },
  BA: { nome: "Bahia", regiao: "Nordeste" },
  CE: { nome: "Ceará", regiao: "Nordeste" },
  DF: { nome: "Distrito Federal", regiao: "Centro-Oeste" },
  ES: { nome: "Espírito Santo", regiao: "Sudeste" },
  GO: { nome: "Goiás", regiao: "Centro-Oeste" },
  MA: { nome: "Maranhão", regiao: "Nordeste" },
  MT: { nome: "Mato Grosso", regiao: "Centro-Oeste" },
  MS: { nome: "Mato Grosso do Sul", regiao: "Centro-Oeste" },
  MG: { nome: "Minas Gerais", regiao: "Sudeste" },
  PA: { nome: "Pará", regiao: "Norte" },
  PB: { nome: "Paraíba", regiao: "Nordeste" },
  PR: { nome: "Paraná", regiao: "Sul" },
  PE: { nome: "Pernambuco", regiao: "Nordeste" },
  PI: { nome: "Piauí", regiao: "Nordeste" },
  RJ: { nome: "Rio de Janeiro", regiao: "Sudeste" },
  RN: { nome: "Rio Grande do Norte", regiao: "Nordeste" },
  RS: { nome: "Rio Grande do Sul", regiao: "Sul" },
  RO: { nome: "Rondônia", regiao: "Norte" },
  RR: { nome: "Roraima", regiao: "Norte" },
  SC: { nome: "Santa Catarina", regiao: "Sul" },
  SP: { nome: "São Paulo", regiao: "Sudeste" },
  SE: { nome: "Sergipe", regiao: "Nordeste" },
  TO: { nome: "Tocantins", regiao: "Norte" }
};

const CORES_REGIAO = {
  Norte: { bg: "bg-cyan-500", text: "text-cyan-700", hex: "#06b6d4" },
  Nordeste: { bg: "bg-amber-500", text: "text-amber-700", hex: "#f59e0b" },
  "Centro-Oeste": { bg: "bg-emerald-500", text: "text-emerald-700", hex: "#10b981" },
  Sudeste: { bg: "bg-orange-500", text: "text-orange-700", hex: "#f97316" },
  Sul: { bg: "bg-red-500", text: "text-red-700", hex: "#ef4444" }
};

const SEGMENTOS = [
  { value: "INSS", label: "INSS - Previdência Social", color: "bg-blue-500" },
  { value: "SIAPE", label: "SIAPE - Servidores Federais", color: "bg-green-500" },
  { value: "LOAS", label: "LOAS/BPC", color: "bg-purple-500" },
  { value: "ESTADUAL", label: "Servidores Estaduais", color: "bg-orange-500" },
  { value: "MUNICIPAL", label: "Servidores Municipais", color: "bg-pink-500" },
  { value: "FORCAS_ARMADAS", label: "Forças Armadas", color: "bg-gray-600" },
  { value: "OUTROS", label: "Outros", color: "bg-slate-500" }
];

// ==================== COMPONENTES ====================

function StatCard({ title, value, subtitle, icon: Icon, color, trend, trendValue, onClick }) {
  return (
    <Card 
      className={cn("cursor-pointer transition-all hover:shadow-md", onClick && "hover:border-blue-300")}
      onClick={onClick}
    >
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className={cn("p-2 rounded-lg", color)}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          {trend && (
            <div className={cn(
              "flex items-center text-xs font-medium",
              trend === "up" ? "text-green-600" : "text-red-600"
            )}>
              {trend === "up" ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function MigrationFunnel({ stats }) {
  const { base, externos, internos, total, taxaConversao } = stats;
  const percentBase = total > 0 ? (base / total) * 100 : 0;
  const percentExternos = total > 0 ? (externos / total) * 100 : 0;
  const percentInternos = total > 0 ? (internos / total) * 100 : 0;
  
  // Taxas de conversão entre etapas
  // Taxa Base→Externo: quantos % da base original já se tornaram externos
  const totalOriginal = base + externos + internos;
  const taxaBaseExterno = totalOriginal > 0 ? ((externos + internos) / totalOriginal * 100) : 0;
  // Taxa Externo→Interno
  const taxaExternoInterno = externos > 0 ? ((internos / externos) * 100) : 0;
  // Taxa total vem do backend (já calculada corretamente)
  const taxaTotal = taxaConversao || 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="h-5 w-5 text-yellow-500" />
          Funil de Migração
        </CardTitle>
        <CardDescription>Fluxo de leads: Base → Externos → Internos</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Funil Visual */}
          <div className="flex items-center justify-between gap-2">
            {/* Base */}
            <div className="flex-1 text-center">
              <div className="bg-gray-100 rounded-lg p-3">
                <Database className="h-6 w-6 mx-auto text-gray-600 mb-1" />
                <p className="text-2xl font-bold text-gray-700">{formatNumber(base)}</p>
                <p className="text-xs text-gray-500">Base</p>
                <p className="text-xs text-gray-400">{formatPercent(percentBase)}</p>
              </div>
            </div>
            
            {/* Seta + Taxa */}
            <div className="flex flex-col items-center">
              <ArrowRight className="h-5 w-5 text-gray-400" />
              <span className="text-xs text-green-600 font-medium">{formatPercent(taxaBaseExterno)}</span>
            </div>
            
            {/* Externos */}
            <div className="flex-1 text-center">
              <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                <UserPlus className="h-6 w-6 mx-auto text-orange-600 mb-1" />
                <p className="text-2xl font-bold text-orange-700">{formatNumber(externos)}</p>
                <p className="text-xs text-orange-600">Externos</p>
                <p className="text-xs text-orange-400">{formatPercent(percentExternos)}</p>
              </div>
            </div>
            
            {/* Seta + Taxa */}
            <div className="flex flex-col items-center">
              <ArrowRight className="h-5 w-5 text-gray-400" />
              <span className="text-xs text-green-600 font-medium">{formatPercent(taxaExternoInterno)}</span>
            </div>
            
            {/* Internos */}
            <div className="flex-1 text-center">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <UserCheck className="h-6 w-6 mx-auto text-blue-600 mb-1" />
                <p className="text-2xl font-bold text-blue-700">{formatNumber(internos)}</p>
                <p className="text-xs text-blue-600">Internos</p>
                <p className="text-xs text-blue-400">{formatPercent(percentInternos)}</p>
              </div>
            </div>
          </div>
          
          {/* Barra de Progresso */}
          <div className="mt-4">
            <div className="flex h-4 rounded-full overflow-hidden bg-gray-100">
              <div 
                className="bg-gray-400 transition-all"
                style={{ width: `${percentBase}%` }}
                title={`Base: ${base}`}
              />
              <div 
                className="bg-orange-500 transition-all"
                style={{ width: `${percentExternos}%` }}
                title={`Externos: ${externos}`}
              />
              <div 
                className="bg-blue-500 transition-all"
                style={{ width: `${percentInternos}%` }}
                title={`Internos: ${internos}`}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-gray-400" /> Base
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-orange-500" /> Externos
              </span>
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 rounded bg-blue-500" /> Internos
              </span>
            </div>
          </div>
          
          {/* Taxa de Conversão Total */}
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-green-700">Taxa de Conversão Total (Base → Cliente)</span>
              <span className="text-lg font-bold text-green-700">{formatPercent(taxaTotal)}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BrazilMapSimple({ data = {}, onStateClick, selectedState }) {
  const [hoveredState, setHoveredState] = useState(null);
  
  // Paths SVG simplificados dos estados brasileiros (coordenadas aproximadas)
  const statePaths = {
    AC: "M45,195 L75,185 L85,200 L75,220 L45,215 Z",
    AM: "M50,120 L180,100 L200,150 L150,200 L80,190 L50,160 Z",
    RR: "M100,50 L150,40 L160,80 L130,100 L90,90 Z",
    AP: "M220,60 L260,50 L270,100 L240,110 L210,90 Z",
    PA: "M180,80 L300,60 L320,150 L260,180 L180,160 Z",
    MA: "M300,100 L370,90 L380,140 L340,170 L290,150 Z",
    PI: "M350,140 L390,130 L400,200 L360,220 L340,180 Z",
    CE: "M400,110 L440,100 L450,150 L420,170 L390,150 Z",
    RN: "M450,130 L480,125 L485,155 L455,165 Z",
    PB: "M440,160 L480,155 L485,180 L445,185 Z",
    PE: "M400,175 L480,180 L475,210 L400,215 Z",
    AL: "M450,215 L480,210 L485,240 L455,245 Z",
    SE: "M440,245 L470,240 L475,265 L445,270 Z",
    BA: "M340,200 L440,190 L450,300 L380,340 L310,280 Z",
    MT: "M140,200 L260,180 L280,280 L180,300 L130,260 Z",
    GO: "M260,260 L340,250 L360,330 L300,360 L250,320 Z",
    DF: "M320,295 L340,290 L345,310 L325,315 Z",
    TO: "M280,160 L340,150 L360,240 L310,260 L270,220 Z",
    MS: "M180,300 L260,280 L280,380 L200,400 L160,360 Z",
    MG: "M320,300 L420,280 L440,380 L360,420 L300,380 Z",
    ES: "M430,340 L470,330 L475,380 L435,390 Z",
    RJ: "M400,390 L450,380 L455,420 L410,430 Z",
    SP: "M300,380 L400,360 L420,430 L340,460 L280,430 Z",
    PR: "M260,430 L350,410 L370,470 L290,490 L250,460 Z",
    SC: "M290,490 L360,475 L375,520 L305,540 Z",
    RS: "M260,510 L340,500 L360,580 L280,600 L240,560 Z",
    RO: "M100,200 L160,190 L170,260 L120,280 L90,250 Z"
  };

  const getStateColor = (uf) => {
    const estado = ESTADOS_BRASIL[uf];
    const stateData = data[uf] || { total: 0 };
    if (!estado) return "#e5e7eb";
    
    const regiao = CORES_REGIAO[estado.regiao];
    if (stateData.total > 0) {
      return regiao?.hex || "#3b82f6";
    }
    return "#e5e7eb";
  };

  const getStateTextPosition = (uf) => {
    const positions = {
      AC: { x: 60, y: 205 }, AM: { x: 120, y: 155 }, RR: { x: 125, y: 70 },
      AP: { x: 240, y: 80 }, PA: { x: 240, y: 120 }, MA: { x: 335, y: 130 },
      PI: { x: 370, y: 175 }, CE: { x: 420, y: 135 }, RN: { x: 465, y: 145 },
      PB: { x: 460, y: 170 }, PE: { x: 440, y: 195 }, AL: { x: 465, y: 230 },
      SE: { x: 455, y: 255 }, BA: { x: 380, y: 260 }, MT: { x: 200, y: 240 },
      GO: { x: 300, y: 305 }, DF: { x: 332, y: 305 }, TO: { x: 305, y: 200 },
      MS: { x: 220, y: 340 }, MG: { x: 370, y: 345 }, ES: { x: 450, y: 360 },
      RJ: { x: 425, y: 405 }, SP: { x: 345, y: 415 }, PR: { x: 305, y: 455 },
      SC: { x: 325, y: 505 }, RS: { x: 300, y: 550 }, RO: { x: 130, y: 240 }
    };
    return positions[uf] || { x: 0, y: 0 };
  };

  return (
    <div className="relative">
      <svg viewBox="0 0 520 620" className="w-full h-auto max-h-[400px]">
        {/* Background */}
        <rect width="520" height="620" fill="#f8fafc" rx="8" />
        
        {/* States */}
        {Object.entries(statePaths).map(([uf, path]) => {
          const estado = ESTADOS_BRASIL[uf];
          const stateData = data[uf] || { total: 0, base: 0, externos: 0, internos: 0 };
          const isSelected = selectedState === uf;
          const isHovered = hoveredState === uf;
          const textPos = getStateTextPosition(uf);
          
          return (
            <g key={uf}>
              <path
                d={path}
                fill={getStateColor(uf)}
                stroke={isSelected ? "#2563eb" : isHovered ? "#1e40af" : "#ffffff"}
                strokeWidth={isSelected ? 3 : isHovered ? 2 : 1}
                className="cursor-pointer transition-all duration-200"
                style={{
                  filter: isHovered ? "brightness(1.1)" : "none",
                  transform: isHovered ? "scale(1.02)" : "scale(1)",
                  transformOrigin: `${textPos.x}px ${textPos.y}px`
                }}
                onClick={() => onStateClick && onStateClick(uf)}
                onMouseEnter={() => setHoveredState(uf)}
                onMouseLeave={() => setHoveredState(null)}
              />
              <text
                x={textPos.x}
                y={textPos.y}
                fill={stateData.total > 0 ? "#ffffff" : "#6b7280"}
                fontSize="11"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                className="pointer-events-none select-none"
              >
                {uf}
              </text>
              {stateData.total > 0 && (
                <text
                  x={textPos.x}
                  y={textPos.y + 12}
                  fill="#ffffff"
                  fontSize="9"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="pointer-events-none select-none opacity-90"
                >
                  {stateData.total}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      
      {/* Tooltip */}
      {hoveredState && (
        <div className="absolute top-2 left-2 bg-white shadow-xl rounded-lg p-3 border z-50 min-w-[180px]">
          <p className="font-bold text-gray-800">{ESTADOS_BRASIL[hoveredState]?.nome}</p>
          <p className="text-xs text-gray-500 mb-2">{ESTADOS_BRASIL[hoveredState]?.regiao}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Total:</span>
              <span className="font-bold">{data[hoveredState]?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Base:</span>
              <span>{data[hoveredState]?.base || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-orange-600">Externos:</span>
              <span className="text-orange-700">{data[hoveredState]?.externos || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-600">Internos:</span>
              <span className="text-blue-700">{data[hoveredState]?.internos || 0}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Legenda */}
      <div className="flex flex-wrap justify-center gap-3 mt-2 text-xs">
        {Object.entries(CORES_REGIAO).map(([regiao, cores]) => (
          <div key={regiao} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: cores.hex }} />
            <span className="text-gray-600">{regiao}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SegmentCard({ segmento, stats, onClick, isSelected }) {
  const seg = SEGMENTOS.find(s => s.value === segmento) || { label: segmento, color: "bg-gray-500" };
  
  return (
    <div 
      className={cn(
        "p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md",
        isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white"
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className={cn("w-3 h-3 rounded-full", seg.color)} />
        <div className="flex-1">
          <p className="font-medium text-sm">{seg.label}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-lg font-bold">{formatNumber(stats.total)}</span>
            <span className="text-xs text-gray-400">leads</span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </div>
      <div className="mt-2 flex gap-2 text-xs">
        <Badge variant="outline" className="bg-gray-50">{stats.base} base</Badge>
        <Badge variant="outline" className="bg-orange-50 text-orange-700">{stats.externos} ext</Badge>
        <Badge variant="outline" className="bg-blue-50 text-blue-700">{stats.internos} int</Badge>
      </div>
    </div>
  );
}

function ImportModal({ open, onClose, onImport, segmentos }) {
  const [selectedSegmento, setSelectedSegmento] = useState("");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setValidationResult(null);
    
    // Simular preview dos primeiros registros
    // Na implementação real, isso seria feito no backend
    setPreview({
      total: 0,
      sample: []
    });
  };

  const handleValidate = async () => {
    if (!file || !selectedSegmento) {
      toast.error("Selecione um segmento e um arquivo");
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("segmento", selectedSegmento);
      formData.append("validate_only", "true");
      
      const response = await axios.post(`${API}/leads-base/import/validate`, formData);
      setValidationResult(response.data);
      
      if (response.data.duplicados > 0) {
        toast.warning(`${response.data.duplicados} CPFs duplicados encontrados`);
      } else {
        toast.success(`${response.data.validos} registros válidos para importação`);
      }
    } catch (error) {
      console.error("Erro na validação:", error);
      toast.error(error.response?.data?.detail || "Erro ao validar arquivo");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!file || !selectedSegmento) {
      toast.error("Selecione um segmento e um arquivo");
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("segmento", selectedSegmento);
      
      const response = await axios.post(`${API}/leads-base/import`, formData);
      toast.success(`${response.data.importados} leads importados com sucesso!`);
      onImport && onImport(response.data);
      onClose();
    } catch (error) {
      console.error("Erro na importação:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar arquivo");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Importar Base de Leads
          </DialogTitle>
          <DialogDescription>
            Importe leads de uma planilha Excel ou CSV. CPFs duplicados serão ignorados.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Seleção de Segmento */}
          <div className="space-y-2">
            <Label>Segmento da Base *</Label>
            <Select value={selectedSegmento} onValueChange={setSelectedSegmento}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o segmento" />
              </SelectTrigger>
              <SelectContent>
                {SEGMENTOS.map(seg => (
                  <SelectItem key={seg.value} value={seg.value}>
                    <div className="flex items-center gap-2">
                      <div className={cn("w-2 h-2 rounded-full", seg.color)} />
                      {seg.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Upload de Arquivo */}
          <div className="space-y-2">
            <Label>Arquivo (Excel/CSV) *</Label>
            <div 
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                file ? "border-green-300 bg-green-50" : "border-gray-300 hover:border-blue-400"
              )}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="h-8 w-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-green-700">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setFile(null); setValidationResult(null); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">Clique para selecionar ou arraste o arquivo</p>
                  <p className="text-xs text-gray-400 mt-1">Formatos: .xlsx, .xls, .csv</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          
          {/* Resultado da Validação */}
          {validationResult && (
            <div className="p-4 rounded-lg border bg-gray-50 space-y-2">
              <p className="font-medium text-sm">Resultado da Validação:</p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center p-2 bg-white rounded">
                  <p className="text-2xl font-bold text-gray-700">{validationResult.total}</p>
                  <p className="text-xs text-gray-500">Total no arquivo</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded">
                  <p className="text-2xl font-bold text-green-600">{validationResult.validos}</p>
                  <p className="text-xs text-green-600">Válidos</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded">
                  <p className="text-2xl font-bold text-red-600">{validationResult.duplicados}</p>
                  <p className="text-xs text-red-600">Duplicados</p>
                </div>
              </div>
              {validationResult.duplicados > 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
                  <AlertTriangle className="h-3 w-3" />
                  CPFs duplicados serão ignorados na importação
                </p>
              )}
            </div>
          )}
          
          {/* Campos Esperados */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs font-medium text-blue-700 mb-2">Campos esperados na planilha:</p>
            <div className="flex flex-wrap gap-1">
              {["CPF*", "Nome*", "Telefone", "UF", "Cidade", "Data Nascimento", "Banco", "Nº Contrato", "Valor Parcela", "Saldo Devedor"].map(campo => (
                <Badge key={campo} variant="outline" className="text-xs bg-white">
                  {campo}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={importing}>
            Cancelar
          </Button>
          <Button 
            variant="outline" 
            onClick={handleValidate} 
            disabled={!file || !selectedSegmento || importing}
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Validar
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={!file || !selectedSegmento || importing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CidadesModal({ open, onClose, estado, cidades }) {
  if (!estado) return null;
  
  const estadoInfo = ESTADOS_BRASIL[estado];
  const cidadesOrdenadas = [...(cidades || [])].sort((a, b) => b.total - a.total);
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {estadoInfo?.nome || estado} ({estado})
          </DialogTitle>
          <DialogDescription>
            Distribuição de leads por cidade - {estadoInfo?.regiao}
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[400px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cidade</TableHead>
                <TableHead className="text-center">Base</TableHead>
                <TableHead className="text-center">Externos</TableHead>
                <TableHead className="text-center">Internos</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cidadesOrdenadas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    Nenhuma cidade encontrada
                  </TableCell>
                </TableRow>
              ) : (
                cidadesOrdenadas.map((cidade, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{cidade.nome || "Não informado"}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-gray-50">{cidade.base}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-orange-50 text-orange-700">{cidade.externos}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">{cidade.internos}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold">{cidade.total}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== COMPONENTE PRINCIPAL ====================

export default function DashboardLeadsBase() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [periodo, setPeriodo] = useState("todos");
  const [filtroSegmento, setFiltroSegmento] = useState("all");
  
  // Dados
  const [stats, setStats] = useState({
    total: 0,
    base: 0,
    externos: 0,
    internos: 0,
    novosHoje: 0,
    novosSemana: 0,
    novosMes: 0,
    taxaConversao: 0
  });
  const [porSegmento, setPorSegmento] = useState({});
  const [porEstado, setPorEstado] = useState({});
  const [porRegiao, setPorRegiao] = useState({});
  const [topEstados, setTopEstados] = useState([]);
  
  // Modais
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importINSSOpen, setImportINSSOpen] = useState(false);
  const [cidadesModalOpen, setCidadesModalOpen] = useState(false);
  const [selectedEstado, setSelectedEstado] = useState(null);
  const [cidadesEstado, setCidadesEstado] = useState([]);
  
  // Estado para importação INSS
  const [arquivoINSS, setArquivoINSS] = useState(null);
  const [validacaoINSS, setValidacaoINSS] = useState(null);
  const [resultadoINSS, setResultadoINSS] = useState(null);
  const [processandoINSS, setProcessandoINSS] = useState(false);

  // Estado para importação SIAPE (131 - Servidor Federal Civil)
  const [importSIAPEOpen, setImportSIAPEOpen] = useState(false);
  const [arquivoSIAPE, setArquivoSIAPE] = useState(null);
  const [validacaoSIAPE, setValidacaoSIAPE] = useState(null);
  const [resultadoSIAPE, setResultadoSIAPE] = useState(null);
  const [processandoSIAPE, setProcessandoSIAPE] = useState(false);

  useEffect(() => {
    carregarDados();
  }, [periodo, filtroSegmento]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Buscar estatísticas da API
      const params = new URLSearchParams();
      if (periodo !== "todos") params.append("periodo", periodo);
      if (filtroSegmento !== "all") params.append("segmento", filtroSegmento);
      
      const response = await axios.get(`${API}/leads-base/stats?${params.toString()}`);
      const data = response.data;
      
      setStats({
        total: data.total || 0,
        base: data.base || 0,
        externos: data.externos || 0,
        internos: data.internos || 0,
        novosHoje: data.novos_hoje || 0,
        novosSemana: data.novos_semana || 0,
        novosMes: data.novos_mes || 0,
        taxaConversao: data.taxa_conversao || 0
      });
      
      setPorSegmento(data.por_segmento || {});
      setPorEstado(data.por_estado || {});
      setPorRegiao(data.por_regiao || {});
      setTopEstados(data.top_estados || []);
      
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      // Usar dados mock para desenvolvimento
      setStats({
        total: 1250,
        base: 800,
        externos: 350,
        internos: 100,
        novosHoje: 45,
        novosSemana: 180,
        novosMes: 520,
        taxaConversao: 12.5
      });
      setPorSegmento({
        INSS: { total: 650, base: 400, externos: 200, internos: 50 },
        SIAPE: { total: 320, base: 220, externos: 80, internos: 20 },
        LOAS: { total: 180, base: 120, externos: 45, internos: 15 },
        ESTADUAL: { total: 100, base: 60, externos: 25, internos: 15 }
      });
      setPorEstado({
        SP: { total: 320, base: 200, externos: 90, internos: 30 },
        RJ: { total: 180, base: 110, externos: 50, internos: 20 },
        MG: { total: 150, base: 100, externos: 35, internos: 15 },
        BA: { total: 120, base: 80, externos: 30, internos: 10 },
        RS: { total: 100, base: 70, externos: 20, internos: 10 },
        PR: { total: 90, base: 60, externos: 20, internos: 10 },
        PE: { total: 80, base: 55, externos: 18, internos: 7 },
        CE: { total: 70, base: 50, externos: 15, internos: 5 }
      });
      setTopEstados([
        { uf: "SP", nome: "São Paulo", total: 320 },
        { uf: "RJ", nome: "Rio de Janeiro", total: 180 },
        { uf: "MG", nome: "Minas Gerais", total: 150 },
        { uf: "BA", nome: "Bahia", total: 120 },
        { uf: "RS", nome: "Rio Grande do Sul", total: 100 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await carregarDados();
    setRefreshing(false);
    toast.success("Dados atualizados!");
  };

  const handleStateClick = async (uf) => {
    setSelectedEstado(uf);
    
    try {
      const response = await axios.get(`${API}/leads-base/cidades/${uf}`);
      setCidadesEstado(response.data.cidades || []);
    } catch (error) {
      // Mock data
      setCidadesEstado([
        { nome: "Capital", base: 50, externos: 20, internos: 10, total: 80 },
        { nome: "Interior 1", base: 30, externos: 10, internos: 5, total: 45 },
        { nome: "Interior 2", base: 20, externos: 8, internos: 3, total: 31 }
      ]);
    }
    
    setCidadesModalOpen(true);
  };

  const handleImportSuccess = () => {
    carregarDados();
  };

  // Funções para importação INSS
  const validarArquivoINSS = async () => {
    if (!arquivoINSS) return;
    
    setProcessandoINSS(true);
    try {
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      
      const formData = new FormData();
      formData.append('file', arquivoINSS);
      formData.append('empresa_id', empresaAtual?.id || '');
      
      const response = await axios.post(`${API}/importar-inss/validar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setValidacaoINSS(response.data);
      
      if (!response.data.valido) {
        toast.error(response.data.erro || 'Arquivo inválido');
      } else {
        toast.success(`${response.data.cpfs_validos} CPFs válidos encontrados`);
      }
    } catch (error) {
      console.error('Erro na validação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao validar arquivo');
    } finally {
      setProcessandoINSS(false);
    }
  };

  const importarBaseINSS = async () => {
    if (!arquivoINSS) return;
    
    setProcessandoINSS(true);
    try {
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      
      const formData = new FormData();
      formData.append('file', arquivoINSS);
      formData.append('empresa_id', empresaAtual?.id || '');
      
      const response = await axios.post(`${API}/importar-inss`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResultadoINSS(response.data);
      toast.success(response.data.message || 'Importação concluída!');
      carregarDados(); // Atualizar estatísticas
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setProcessandoINSS(false);
    }
  };

  const resetImportINSS = () => {
    setImportINSSOpen(false);
    setArquivoINSS(null);
    setValidacaoINSS(null);
    setResultadoINSS(null);
  };

  // Funções para importação SIAPE (131 - Servidor Federal Civil)
  const validarArquivoSIAPE = async () => {
    if (!arquivoSIAPE) return;
    setProcessandoSIAPE(true);
    try {
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const formData = new FormData();
      formData.append('file', arquivoSIAPE);
      formData.append('empresa_id', empresaAtual?.id || '');
      const response = await axios.post(`${API}/importar-siape/validar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setValidacaoSIAPE(response.data);
      if (!response.data.valido) {
        toast.error(response.data.erro || 'Arquivo inválido');
      } else {
        toast.success(`${response.data.cpfs_validos} CPFs válidos encontrados`);
      }
    } catch (error) {
      console.error('Erro na validação SIAPE:', error);
      toast.error(error.response?.data?.detail || 'Erro ao validar arquivo');
    } finally {
      setProcessandoSIAPE(false);
    }
  };

  const importarBaseSIAPE = async () => {
    if (!arquivoSIAPE) return;
    setProcessandoSIAPE(true);
    try {
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const formData = new FormData();
      formData.append('file', arquivoSIAPE);
      formData.append('empresa_id', empresaAtual?.id || '');
      const response = await axios.post(`${API}/importar-siape`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResultadoSIAPE(response.data);
      toast.success(response.data.message || 'Importação concluída!');
      carregarDados();
    } catch (error) {
      console.error('Erro na importação SIAPE:', error);
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setProcessandoSIAPE(false);
    }
  };

  const resetImportSIAPE = () => {
    setImportSIAPEOpen(false);
    setArquivoSIAPE(null);
    setValidacaoSIAPE(null);
    setResultadoSIAPE(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-blue-600" />
            Dashboard de Leads - Base
          </h1>
          <p className="text-gray-500 text-sm">
            Gestão de bases importadas por segmento com controle de migração
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="hoje">Hoje</SelectItem>
              <SelectItem value="semana">Esta Semana</SelectItem>
              <SelectItem value="mes">Este Mês</SelectItem>
              <SelectItem value="trimestre">Trimestre</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filtroSegmento} onValueChange={setFiltroSegmento}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Todos Segmentos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Segmentos</SelectItem>
              {SEGMENTOS.map(seg => (
                <SelectItem key={seg.value} value={seg.value}>{seg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
          <Button onClick={() => setImportModalOpen(true)} className="bg-gray-600 hover:bg-gray-700">
            <Upload className="h-4 w-4 mr-2" />
            Importar Base
          </Button>
          <Button onClick={() => setImportINSSOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Importar Base INSS
          </Button>
          <Button onClick={() => setImportSIAPEOpen(true)} className="bg-green-600 hover:bg-green-700" data-testid="import-siape-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Importar Base SIAPE
          </Button>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total de Leads"
          value={formatNumber(stats.total)}
          subtitle="Na base de dados"
          icon={Users}
          color="bg-blue-500"
        />
        <StatCard
          title="Base (Importados)"
          value={formatNumber(stats.base)}
          subtitle={`${formatPercent((stats.base / stats.total) * 100)} do total`}
          icon={Database}
          color="bg-gray-500"
        />
        <StatCard
          title="Externos"
          value={formatNumber(stats.externos)}
          subtitle="Prospectados"
          icon={UserPlus}
          color="bg-orange-500"
        />
        <StatCard
          title="Internos"
          value={formatNumber(stats.internos)}
          subtitle="Clientes"
          icon={UserCheck}
          color="bg-green-500"
        />
        <StatCard
          title="Novos Hoje"
          value={formatNumber(stats.novosHoje)}
          subtitle="Importados hoje"
          icon={Calendar}
          color="bg-purple-500"
          trend="up"
          trendValue={`+${stats.novosHoje}`}
        />
        <StatCard
          title="Taxa Conversão"
          value={formatPercent(stats.taxaConversao)}
          subtitle="Base → Cliente"
          icon={TrendingUp}
          color="bg-emerald-500"
        />
      </div>

      {/* Funil de Migração */}
      <MigrationFunnel stats={stats} />

      {/* Grid Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mapa do Brasil */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-500" />
              Distribuição Geográfica
            </CardTitle>
            <CardDescription>Clique em um estado para ver as cidades</CardDescription>
          </CardHeader>
          <CardContent>
            <BrazilMapSimple 
              data={porEstado}
              onStateClick={handleStateClick}
              selectedState={selectedEstado}
            />
          </CardContent>
        </Card>

        {/* Leads por Segmento */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              Leads por Segmento
            </CardTitle>
            <CardDescription>Distribuição por tipo de benefício</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(porSegmento).map(([segmento, data]) => (
                <SegmentCard
                  key={segmento}
                  segmento={segmento}
                  stats={data}
                  onClick={() => setFiltroSegmento(segmento)}
                  isSelected={filtroSegmento === segmento}
                />
              ))}
              {Object.keys(porSegmento).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Database className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Nenhum segmento encontrado</p>
                  <p className="text-xs">Importe uma base para começar</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Estados */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-green-500" />
            Top Estados
          </CardTitle>
          <CardDescription>Estados com maior volume de leads</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {topEstados.map((estado, idx) => (
              <div 
                key={estado.uf}
                className="p-4 rounded-lg border hover:border-blue-300 cursor-pointer transition-all"
                onClick={() => handleStateClick(estado.uf)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold",
                    idx === 0 ? "bg-yellow-500" : idx === 1 ? "bg-gray-400" : idx === 2 ? "bg-amber-600" : "bg-gray-300"
                  )}>
                    {idx + 1}
                  </span>
                  <span className="font-bold">{estado.uf}</span>
                </div>
                <p className="text-sm text-gray-600">{estado.nome}</p>
                <p className="text-xl font-bold text-blue-600">{formatNumber(estado.total)}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Modais */}
      <ImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImportSuccess}
        segmentos={SEGMENTOS}
      />
      
      <CidadesModal
        open={cidadesModalOpen}
        onClose={() => setCidadesModalOpen(false)}
        estado={selectedEstado}
        cidades={cidadesEstado}
      />

      {/* Modal Importar Base INSS */}
      <Dialog open={importINSSOpen} onOpenChange={resetImportINSS}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              Importar Base INSS (037 - INSS PREVIDÊNCIA SOCIAL)
            </DialogTitle>
            <DialogDescription>
              Importe leads de arquivo Excel/CSV. CPFs duplicados não serão cadastrados novamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Instruções */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Regras de Importação:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Instituição padrão: <strong>037 - INSS (PREVIDÊNCIA SOCIAL)</strong></li>
                <li>• CPF duplicado: NÃO cadastra novamente</li>
                <li>• Se CPF existe: adiciona telefones/contratos NOVOS</li>
                <li>• CEP: busca endereço automaticamente</li>
                <li>• Esteira: <strong>BASE</strong> | Classificação: <strong>Leads/Base</strong></li>
              </ul>
            </div>

            {/* Upload */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setArquivoINSS(e.target.files?.[0] || null)}
                className="hidden"
                id="arquivo-inss-leads"
              />
              <label htmlFor="arquivo-inss-leads" className="cursor-pointer">
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium">
                  {arquivoINSS ? arquivoINSS.name : 'Clique para selecionar arquivo Excel/CSV'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Formatos: .xlsx, .xls, .csv
                </p>
              </label>
            </div>

            {/* Validação Preview */}
            {validacaoINSS && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700">{validacaoINSS.total_linhas}</p>
                    <p className="text-xs text-slate-500">Total Linhas</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{validacaoINSS.cpfs_validos}</p>
                    <p className="text-xs text-green-600">CPFs Válidos</p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-700">{validacaoINSS.cpfs_existentes}</p>
                    <p className="text-xs text-yellow-600">CPFs Existentes</p>
                  </div>
                  <div className="bg-red-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{validacaoINSS.cpfs_invalidos}</p>
                    <p className="text-xs text-red-600">CPFs Inválidos</p>
                  </div>
                </div>
              </div>
            )}

            {/* Resultado da Importação */}
            {resultadoINSS && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Importação Concluída!</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-green-600 font-medium text-xl">{resultadoINSS.estatisticas?.leads_novos || 0}</span>
                    <span className="text-green-700 ml-1">leads novos</span>
                  </div>
                  <div>
                    <span className="text-yellow-600 font-medium text-xl">{resultadoINSS.estatisticas?.leads_atualizados || 0}</span>
                    <span className="text-yellow-700 ml-1">atualizados</span>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium text-xl">{resultadoINSS.estatisticas?.contratos_novos || 0}</span>
                    <span className="text-blue-700 ml-1">contratos</span>
                  </div>
                </div>
                {resultadoINSS.estatisticas?.erros?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600 max-h-20 overflow-y-auto">
                    Erros: {resultadoINSS.estatisticas.erros.slice(0, 5).join('; ')}
                  </div>
                )}
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-3 justify-end">
              {!resultadoINSS && (
                <>
                  <Button
                    variant="outline"
                    onClick={validarArquivoINSS}
                    disabled={!arquivoINSS || processandoINSS}
                  >
                    {processandoINSS ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Validar Arquivo
                  </Button>
                  <Button
                    onClick={importarBaseINSS}
                    disabled={!arquivoINSS || processandoINSS}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {processandoINSS ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Importar
                  </Button>
                </>
              )}
              {resultadoINSS && (
                <Button onClick={resetImportINSS}>
                  Fechar
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Importar Base SIAPE */}
      <Dialog open={importSIAPEOpen} onOpenChange={resetImportSIAPE}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              Importar Base SIAPE (131 - SERVIDOR FEDERAL CIVIL)
            </DialogTitle>
            <DialogDescription>
              Importe leads de servidores federais (arquivo Excel/CSV). CPFs duplicados não serão cadastrados novamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">Regras de Importação:</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Instituição padrão: <strong>131 - SERVIDOR FEDERAL CIVIL</strong></li>
                <li>• Segmento: <strong>SIAPE</strong></li>
                <li>• CPF duplicado: NÃO cadastra novamente</li>
                <li>• Se CPF existe: adiciona telefones/contratos NOVOS</li>
                <li>• CEP: busca endereço automaticamente</li>
                <li>• Esteira: <strong>BASE</strong> | Classificação: <strong>Leads/Base</strong></li>
              </ul>
            </div>

            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => { setArquivoSIAPE(e.target.files?.[0] || null); setValidacaoSIAPE(null); setResultadoSIAPE(null); }}
                className="hidden"
                id="arquivo-siape-leads"
                data-testid="siape-file-input"
              />
              <label htmlFor="arquivo-siape-leads" className="cursor-pointer">
                <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium">
                  {arquivoSIAPE ? arquivoSIAPE.name : 'Clique para selecionar arquivo Excel/CSV'}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Formatos: .xlsx, .xls, .csv
                </p>
              </label>
            </div>

            {validacaoSIAPE && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700">{validacaoSIAPE.total_linhas}</p>
                    <p className="text-xs text-slate-500">Total Linhas</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{validacaoSIAPE.cpfs_validos}</p>
                    <p className="text-xs text-green-600">CPFs Válidos</p>
                  </div>
                  <div className="bg-yellow-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-700">{validacaoSIAPE.cpfs_existentes}</p>
                    <p className="text-xs text-yellow-600">CPFs Existentes</p>
                  </div>
                  <div className="bg-red-100 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{validacaoSIAPE.cpfs_invalidos}</p>
                    <p className="text-xs text-red-600">CPFs Inválidos</p>
                  </div>
                </div>
              </div>
            )}

            {resultadoSIAPE && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Importação Concluída!</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-green-600 font-medium text-xl">{resultadoSIAPE.estatisticas?.leads_novos || 0}</span>
                    <span className="text-green-700 ml-1">leads novos</span>
                  </div>
                  <div>
                    <span className="text-yellow-600 font-medium text-xl">{resultadoSIAPE.estatisticas?.leads_atualizados || 0}</span>
                    <span className="text-yellow-700 ml-1">atualizados</span>
                  </div>
                  <div>
                    <span className="text-blue-600 font-medium text-xl">{resultadoSIAPE.estatisticas?.contratos_novos || 0}</span>
                    <span className="text-blue-700 ml-1">contratos</span>
                  </div>
                </div>
                {resultadoSIAPE.estatisticas?.erros?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600 max-h-20 overflow-y-auto">
                    Erros: {resultadoSIAPE.estatisticas.erros.slice(0, 5).join('; ')}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              {!resultadoSIAPE && (
                <>
                  <Button
                    variant="outline"
                    onClick={validarArquivoSIAPE}
                    disabled={!arquivoSIAPE || processandoSIAPE}
                    data-testid="siape-validate-btn"
                  >
                    {processandoSIAPE ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Validar Arquivo
                  </Button>
                  <Button
                    onClick={importarBaseSIAPE}
                    disabled={!arquivoSIAPE || processandoSIAPE}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="siape-import-btn"
                  >
                    {processandoSIAPE ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Importar
                  </Button>
                </>
              )}
              {resultadoSIAPE && (
                <Button onClick={resetImportSIAPE}>
                  Fechar
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
